import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, NgModule, OnDestroy, OnInit, Output, Renderer2, RendererFactory2 } from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { RouterModule } from '@angular/router';
import { Page, PageType } from '@app/models/page';
import { SimplifiedTerm } from '@app/models/simplified-term';
import { ChannelService } from '@app/services/channel.service';
import { LanguageTermService } from '@app/services/language-term.service';
import { isAfter, parseISO } from 'date-fns';
import { forkJoin, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { WidgetPlaceholderModule } from '../widget-placeholder/widget-placeholder.component';
import { ImgSrcProxyModule } from '@app/directives/img-src-proxy.directive';
import { LinkModule } from '@app/directives/link.directive';

@Component({
  selector: 'app-page-list',
  templateUrl: './page-list.component.html',
  styleUrls: ['./page-list.component.scss']
})
export class PageListComponent implements OnInit, OnDestroy {
  @Input() pages: Array<Page>;
  @Input() editorOptionsEnabled: boolean;
  @Input() showSharePointExternalIcon: boolean;
  @Input() pageType: PageType;
  @Input() loading: boolean;
  @Output() deleteEvent = new EventEmitter<Page>();

  channels: SimplifiedTerm[];
  languages: SimplifiedTerm[];
  termsLoaded = false;
  subscription = new Subscription();

  private renderer: Renderer2; // Used to convert the light page content HTML to plain text

  constructor(private dialog: MatDialog,
              private channelService: ChannelService,
              private languageTermService: LanguageTermService,
              private rendererFactory: RendererFactory2) {

    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  ngOnInit(): void {

    this.subscription.add(
      forkJoin([
        this.channelService.getChannels(),
        this.languageTermService.getLanguages()])
        .pipe(
          first()
        )
        .subscribe(([channels, languages]) => {
          this.channels = channels;
          this.languages = languages;
          this.termsLoaded = true;
        })
    );
  }

  deletePage(page: Page) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent);
    this.subscription.add(
      dialogRef
        .afterClosed()
        .subscribe(
          result => {
            if (result) {
              this.deleteEvent.emit(page);
            }
          }));
  }

  getLanguageNameForPage(page: Page): string {
    // Set language to empty string if there is no LanguageTerm set for page
    if (!page.LanguageTermId) {
      return '';
    }
    const matchingLanguage = this.languages.find(l => l.Guid.toLowerCase() === page.LanguageTermId.toLowerCase());
    if (matchingLanguage && matchingLanguage.Name) {
      return matchingLanguage.Name;
    }
    return '';
  }

  getChannelNamesForPage(page: Page): string[] {
    const result = new Array<string>();
    page.ChannelTermPages.forEach (ct => {
      const pageChannel = this.channels.findRecursive(fc => fc.Guid === ct.ChannelTermId, 'Terms');
      if (pageChannel && pageChannel.Name) {
        result.push(pageChannel.Name);
      }
    });

    return result;
  }

  isScheduledDraft(date: Date): boolean {
    return date && isAfter((typeof date === 'string' ? parseISO(date) : date), new Date());
  }

  HTMLToPlainText(htmlContent: string): string {
    const htmlParser = this.renderer.createElement('div');
    htmlParser.innerHTML = htmlContent;
    const plainText = htmlParser.innerText;
    htmlParser.remove();
    return plainText;
  }

  /**
   * Creates a user friendly string by concatenting the news item SenderTag labels
   * @param pageItem Newsitem to get SenderTag from
   * @returns string for displaying the tags for users
   */
  getSenderTagString(pageItem: Page): string {
    if (pageItem.SenderTags && pageItem.SenderTags.length) {
      let senderTagString = '';
      let maxSenderTags = 2; // Set max to show 2 sendertags
      // If the length of SenderTags array is less than max, set to length of sendertags
      if (pageItem.SenderTags.length < maxSenderTags) {
        maxSenderTags = pageItem.SenderTags.length;
      }
      for (let i = 0; i < maxSenderTags; i++) {
        if (i > 0) {
          senderTagString += ' | ';
        }
        // check if SenderTag is from graph or search
        if (pageItem.SenderTags[i].Label) {
          senderTagString += pageItem.SenderTags[i].Label;
        } else {
          senderTagString +=  pageItem.SenderTags[i];
        }
      }
      return senderTagString;
    }
    return null;
  }

  ngOnDestroy(): void {
    this.subscription && this.subscription.unsubscribe();
  }
}
@NgModule({
  declarations: [
    PageListComponent
  ],
  exports: [
    PageListComponent
  ],
  imports: [
    CommonModule,
    MatChipsModule,
    MatMenuModule,
    RouterModule,
    WidgetPlaceholderModule,
    ImgSrcProxyModule,
    LinkModule,
  ]
})
export class PageListModule { }
